import PropTypes from 'prop-types';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    AirOutlined,
    DeviceThermostatOutlined,
    FavoriteBorderOutlined,
    FavoriteOutlined,
    MonitorHeartOutlined,
    MonitorWeightOutlined,
    WaterDropOutlined,
    ScienceOutlined
} from '@mui/icons-material';
import {
    Box,
    Button,
    IconButton,
    Skeleton,
    Tab,
    Tabs,
    Tooltip,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { getLatestMeasurements } from '../../../../services/measurements';
import { clinicalMeasurementTypes } from '../../../../configs/clinicalMeasurements';
import { fetchLatestClinicalMeasurements } from '../../../../services/clinicalMeasurements';
import OverviewCard from './OverviewCard';

const PAGE_SIZE = 6;

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const formatValue = (value, unit) =>
    hasValue(value) ? `${value}${unit ? ` ${unit}` : ''}` : null;

const formatBloodPressure = (measurement) => {
    if (!hasValue(measurement?.systolic) || !hasValue(measurement?.diastolic)) {
        return null;
    }

    return `${measurement.systolic}/${measurement.diastolic} mmHg`;
};

const MeasurementItem = ({
    icon,
    label,
    value,
    timestamp,
    loading,
    index,
    t,
    unavailable = false
}) => {
    const formattedTimestamp = (() => {
        if (!timestamp || !dayjs(timestamp).isValid()) return null;

        const date = dayjs(timestamp);
        if (date.isSame(dayjs(), 'day')) {
            return t('userPage.overview.measurements.todayAt', {
                time: date.format('HH:mm')
            });
        }
        if (date.isSame(dayjs().subtract(1, 'day'), 'day')) {
            return t('userPage.overview.measurements.yesterdayAt', {
                time: date.format('HH:mm')
            });
        }
        return date.format('DD/MM, HH:mm');
    })();

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.25,
                minWidth: 0,
                minHeight: 88,
                px: 2,
                py: 1.5,
                borderLeft: index % 3 === 0 ? 0 : '1px solid #D9DEE2',
                borderTop: index < 3 ? 0 : '1px solid #D9DEE2'
            }}
        >
            <Box sx={{ display: 'flex', color: '#1976D2', mt: 0.25 }}>{icon}</Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    sx={{
                        color: '#101418',
                        fontSize: '0.8125rem',
                        fontWeight: 700,
                        overflowWrap: 'anywhere'
                    }}
                >
                    {label}
                </Typography>
                {loading ? (
                    <>
                        <Skeleton width={76} height={25} />
                        <Skeleton width={90} height={19} />
                    </>
                ) : (
                    <>
                        <Typography
                            sx={{
                                color: value ? '#101418' : '#7A878E',
                                fontSize: '0.9375rem',
                                fontWeight: 500,
                                mt: 0.25
                            }}
                        >
                            {unavailable
                                ? '-'
                                : value || t('userPage.overview.measurements.noRecord')}
                        </Typography>
                        {formattedTimestamp && (
                            <Typography
                                sx={{ color: '#7A878E', fontSize: '0.6875rem', mt: 0.25 }}
                            >
                                {formattedTimestamp}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

MeasurementItem.propTypes = {
    icon: PropTypes.node.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    timestamp: PropTypes.string,
    loading: PropTypes.bool.isRequired,
    unavailable: PropTypes.bool,
    index: PropTypes.number.isRequired,
    t: PropTypes.func.isRequired
};

const LatestMeasurementsCard = ({
    patientUuid,
    onViewMeasurements,
    onAddMeasurement
}) => {
    const { t } = useTranslation();
    const [source, setSource] = useState('patient');
    const [page, setPage] = useState(0);
    const patientQuery = useQuery({
        queryKey: ['patient-measurements', patientUuid, 'latest'],
        queryFn: ({ signal }) => getLatestMeasurements(patientUuid, { signal }),
        enabled: source === 'patient' && Boolean(patientUuid),
        staleTime: 60 * 1000
    });
    const clinicalQuery = useQuery({
        queryKey: ['patient-measurements-v2', patientUuid, 'DOCTOR', 'latest'],
        queryFn: ({ signal }) =>
            fetchLatestClinicalMeasurements({
                patient: patientUuid,
                types: clinicalMeasurementTypes.map((type) => type.apiType),
                signal
            }),
        enabled: source === 'clinician' && Boolean(patientUuid),
        staleTime: 60 * 1000
    });
    const { isPending, isError, refetch } =
        source === 'patient' ? patientQuery : clinicalQuery;
    const patientData = patientQuery.data;

    const weight = patientData?.M_WEIGHT?.[0];
    const bloodPressure = patientData?.M_BLOOD_PRESSURE?.[0];
    const bloodOxygen = patientData?.M_BLOOD_OXYGEN?.[0];
    const bloodSugar = patientData?.M_BLOOD_SUGAR?.[0];
    const temperature = patientData?.M_BODY_TEMPERATURE?.[0];
    const heartRate = patientData?.M_HEARTRATE?.[0];

    const patientMeasurements = [
        {
            key: 'heart-rate',
            icon: <FavoriteBorderOutlined fontSize="small" />,
            label: t('userPage.metrics.heartRate'),
            value: formatValue(heartRate?.hr, 'bpm'),
            timestamp: heartRate?.ts
        },
        {
            key: 'blood-pressure',
            icon: <FavoriteOutlined fontSize="small" />,
            label: t('userPage.metrics.bloodPressure'),
            value: formatBloodPressure(bloodPressure),
            timestamp: bloodPressure?.ts
        },
        {
            key: 'blood-oxygen',
            icon: <AirOutlined fontSize="small" />,
            label: t('userPage.metrics.bloodOxygen'),
            value: formatValue(bloodOxygen?.spo2, '%'),
            timestamp: bloodOxygen?.ts
        },
        {
            key: 'blood-sugar',
            icon: <WaterDropOutlined fontSize="small" />,
            label: t('userPage.metrics.bloodSugar'),
            value: formatValue(bloodSugar?.glicose, 'mg/dL'),
            timestamp: bloodSugar?.ts
        },
        {
            key: 'temperature',
            icon: <DeviceThermostatOutlined fontSize="small" />,
            label: t('userPage.metrics.temperature'),
            value: formatValue(temperature?.temperature, '°C'),
            timestamp: temperature?.ts
        },
        {
            key: 'weight',
            icon: <MonitorWeightOutlined fontSize="small" />,
            label: t('userPage.metrics.weight'),
            value: formatValue(weight?.weight, 'kg'),
            timestamp: weight?.ts
        }
    ];

    const clinicalMeasurements = [
        ...clinicalMeasurementTypes.filter((type) => type.overviewAsSingleMetric),
        ...clinicalMeasurementTypes.filter((type) => !type.overviewAsSingleMetric)
    ].flatMap((type) => {
        const Icon = type.icon || ScienceOutlined;
        const record = clinicalQuery.data?.[type.apiType];
        const fields = type.overviewAsSingleMetric
            ? [{ ...type.fields[0], labelKey: type.labelKey }]
            : type.fields;
        return fields.map((field) => ({
            key: `${type.type}-${field.field}`,
            icon: <Icon fontSize="small" />,
            label: field.label || t(field.labelKey),
            value:
                type.apiType === 'M_BLOOD_PRESSURE'
                    ? formatBloodPressure(record?.data)
                    : formatValue(record?.data?.[field.field], field.unit),
            timestamp: record?.ts
        }));
    });
    const measurements =
        source === 'patient' ? patientMeasurements : clinicalMeasurements;
    const pageCount = Math.max(1, Math.ceil(measurements.length / PAGE_SIZE));
    const currentPage = Math.min(page, pageCount - 1);
    const visibleMeasurements = measurements.slice(
        currentPage * PAGE_SIZE,
        (currentPage + 1) * PAGE_SIZE
    );

    return (
        <OverviewCard
            title={t('userPage.overview.latestMeasurements')}
            icon={<MonitorHeartOutlined fontSize="small" />}
            badge={
                <Tabs
                    value={source}
                    aria-label={t('userPage.measurements.sourceSelector')}
                    onChange={(_, value) => {
                        if (value) {
                            setSource(value);
                            setPage(0);
                        }
                    }}
                    sx={{
                        minHeight: 30,
                        bgcolor: '#FFFFFF',
                        border: '1px solid #D7DDE2',
                        borderRadius: 2.25,
                        boxShadow: '0 1px 4px rgba(38, 50, 56, 0.12)',
                        overflow: 'visible',
                        '& .MuiTabs-scroller': { overflow: 'visible !important' },
                        '& .MuiTabs-flexContainer': { overflow: 'visible' },
                        '& .MuiTabs-indicator': {
                            zIndex: 0,
                            height: 'calc(100% + 4px)',
                            bottom: -2,
                            bgcolor: '#0A579B',
                            borderRadius: 1.75,
                            boxShadow: '0 3px 8px rgba(10, 87, 155, 0.28)',
                            transform: 'scaleX(1.02)',
                            transition:
                                'left 240ms cubic-bezier(0.4, 0, 0.2, 1), right 240ms cubic-bezier(0.4, 0, 0.2, 1), width 240ms cubic-bezier(0.4, 0, 0.2, 1)'
                        },
                        '& .MuiTab-root': {
                            zIndex: 1,
                            minWidth: 52,
                            minHeight: 28,
                            px: 1.5,
                            py: 0.5,
                            overflow: 'visible',
                            color: '#263238',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            letterSpacing: 0,
                            lineHeight: 1,
                            textTransform: 'none',
                            transition: 'color 180ms ease',
                            '&.Mui-selected': { color: '#FFFFFF' }
                        }
                    }}
                >
                    <Tab
                        value="patient"
                        label={t('userPage.measurements.patientSource')}
                    />
                    <Tab
                        value="clinician"
                        label={t('userPage.measurements.clinicianSource')}
                    />
                </Tabs>
            }
            action={
                onAddMeasurement && (
                    <Tooltip title={t('userPage.measurements.newMeasurement')}>
                        <IconButton
                            aria-label={t('userPage.measurements.newMeasurement')}
                            onClick={onAddMeasurement}
                            sx={{
                                width: 32,
                                height: 32,
                                ml: 1,
                                bgcolor: '#1976D2',
                                color: '#fff',
                                '&:hover': { bgcolor: '#1565C0' }
                            }}
                        >
                            <AddIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )
            }
            minHeight={230}
        >
            {isError ? (
                <Box
                    sx={{
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5
                    }}
                >
                    <Typography color="error">
                        {t('userPage.overview.measurements.loadError')}
                    </Typography>
                    <Button size="small" variant="outlined" onClick={() => refetch()}>
                        {t('userPage.overview.measurements.retry')}
                    </Button>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                        gridTemplateRows: 'repeat(2, minmax(88px, auto))'
                    }}
                >
                    {visibleMeasurements.map(({ key, ...measurement }, index) => (
                        <MeasurementItem
                            key={key}
                            {...measurement}
                            index={index}
                            loading={isPending}
                            t={t}
                        />
                    ))}
                    {Array.from(
                        { length: PAGE_SIZE - visibleMeasurements.length },
                        (_, offset) => {
                            const index = visibleMeasurements.length + offset;
                            return (
                                <Box
                                    key={`empty-${index}`}
                                    aria-hidden="true"
                                    sx={{
                                        minHeight: 88,
                                        borderLeft:
                                            index % 3 === 0 ? 0 : '1px solid #D9DEE2',
                                        borderTop: index < 3 ? 0 : '1px solid #D9DEE2'
                                    }}
                                />
                            );
                        }
                    )}
                </Box>
            )}

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 'auto',
                    pt: 0.5,
                    gap: 1
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                        size="small"
                        aria-label={t('userPage.measurements.previousPage')}
                        disabled={currentPage === 0}
                        onClick={() => setPage(currentPage - 1)}
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        aria-live="polite"
                    >
                        {t('userPage.measurements.pageOf', {
                            current: currentPage + 1,
                            total: pageCount
                        })}
                    </Typography>
                    <IconButton
                        size="small"
                        aria-label={t('userPage.measurements.nextPage')}
                        disabled={currentPage === pageCount - 1}
                        onClick={() => setPage(currentPage + 1)}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </Box>
                <Button
                    size="small"
                    endIcon={<ArrowForwardIcon fontSize="small" />}
                    onClick={onViewMeasurements}
                    disabled={!onViewMeasurements}
                    sx={{ alignSelf: 'flex-end', mt: 'auto', px: 0.5, fontWeight: 600 }}
                >
                    {t('userPage.overview.viewMeasurements')}
                </Button>
            </Box>
        </OverviewCard>
    );
};

LatestMeasurementsCard.propTypes = {
    patientUuid: PropTypes.string.isRequired,
    onViewMeasurements: PropTypes.func,
    onAddMeasurement: PropTypes.func
};

export default LatestMeasurementsCard;
